import React, { useState, useEffect, useRef, useMemo } from "react";
import { gql, useMutation } from "@apollo/client";
import wand from "../../images/wand.png";
import wave from "../../images/wave.png";
import assistant1 from "../../images/assistant11.jpeg";
import assistant2 from "../../images/assistant22.png";
import assistant3 from "../../images/assistant33.png";
import assistant4 from "../../images/assistant44.png";
import { SparklesIcon, ArrowRightIcon, RefreshIcon } from "@heroicons/react/outline";
import { TypeText } from "../../../../components/type-text/type-text";
import { StaggerList } from "../../../../components/stagger-list/stagger-list";
import { RefdocItem } from "../../../../components/refdoc-item/refdoc-item";
import { IhAssistOutput, ReferenceDocumentEntity } from "../../../../generated/graphql";
import { RefdocItem2 } from "../../../../components/refdoc-item/refdoc-item2";

const GET_ASSIST_DATA = gql`
  mutation IhAssist($message: String!, $asst_id: String!) {
    ihAssist(message: $message, asst_id: $asst_id) {
      documents {
        id
        document_name
        url
        reftype
      }
      initiative {
        id
        name
      }
      answer
      is_ambiguous
    }
  }
`;

interface WelcomeHelpCardProps {
  /** Optional first name for the greeting. Defaults to "How can I help?" only. */
  firstName?: string;
  /** Reference document count by document ID */
  refdocCount?: { [k: string]: number };
}

export const WelcomeHelpCard = ({ firstName, refdocCount = {} }: WelcomeHelpCardProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [nextTextVisible, setNextTextVisible] = useState(false);
  const nextTextRef = useRef<HTMLSpanElement>(null);
  
  // Input and focus state
  const [inputValue, setInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // API response state
  const [response, setResponse] = useState<IhAssistOutput>();
  const [answerTyped, setAnswerTyped] = useState(false);
  const [refdocHeadingTyped, setRefdocHeadingTyped] = useState(false);

  // Array of assistant images
  const assistantImages = [assistant1, assistant2, assistant3, assistant4];
  
  // Sequential image selection - cycle through images on every page load
  const [selectedAssistantImage, setSelectedAssistantImage] = useState<string>(assistantImages[0]);
  
  useEffect(() => {
    const STORAGE_KEY = 'welcomeHelpCard_lastAssistantImageIndex';
    // Get the last loaded index from localStorage, default to -1 if not found
    const lastIndex = parseInt(localStorage.getItem(STORAGE_KEY) || '-1', 10);
    // Calculate next index (increment and wrap around)
    const nextIndex = (lastIndex + 1) % assistantImages.length;
    // Save the new index to localStorage
    localStorage.setItem(STORAGE_KEY, nextIndex.toString());
    // Set the selected image
    setSelectedAssistantImage(assistantImages[nextIndex]);
  }, []); // Empty dependency array ensures this only runs once on mount

  // Array of text options/placeholders - rotated on every page load
  const textOptions = useMemo(() => {
    const baseOptions = [
      `Hello ${firstName}, how can I help?`,
      "What would you like to search?",
      "Need help finding something?",
      "What can I assist you with?",
    ];
    // Rotate the array by a random offset on each mount
    const rotationOffset = Math.floor(Math.random() * baseOptions.length);
    return [
      ...baseOptions.slice(rotationOffset),
      ...baseOptions.slice(0, rotationOffset)
    ];
  }, [firstName]); // Re-rotate when firstName changes

  // GraphQL mutation for assistant API
  const [assist, { loading }] = useMutation(GET_ASSIST_DATA, {
    update: (cache, response) => {
      setResponse(response.data.ihAssist);
      setAnswerTyped(false);
      setRefdocHeadingTyped(false);
    },
  });

  // Placeholder animation - only run when not focused and no input value
  useEffect(() => {
    if (isFocused || inputValue || textOptions.length <= 1) return; // Don't animate if focused, has input, or only one option

    const interval = setInterval(() => {
      // Show next text first (starts at bottom, invisible)
      setNextTextVisible(true);
      
      // Reset ref styles to starting position
      if (nextTextRef.current) {
        nextTextRef.current.style.transform = "translateY(100%)";
        nextTextRef.current.style.opacity = "0";
      }
      
      // Start both animations simultaneously after DOM update
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Start exit animation for current text
          setIsAnimating(true);
          
          // Start entrance animation for next text
          if (nextTextRef.current) {
            nextTextRef.current.style.transform = "translateY(0)";
            nextTextRef.current.style.opacity = "1";
          }
        });
      });
      
      // After animations complete, update index and reset state
      setTimeout(() => {
        // Hide next text overlay first
        setNextTextVisible(false);
        // Update index (next becomes current)
        setCurrentIndex((prev) => (prev + 1) % textOptions.length);
        // Reset animation state immediately (current text should already be in correct position)
        setIsAnimating(false);
      }, 600); // Wait for animations to complete (600ms)
    }, 3000); // Change text every 3 seconds

    return () => clearInterval(interval);
  }, [textOptions.length, isFocused, inputValue]);

  const handleSubmit = () => {
    if (!inputValue.trim() || loading) return;
    
    assist({
      variables: {
        message: inputValue.trim(),
        asst_id: "NA",
      },
    }).catch((error) => {
      console.error("Error calling assist API:", error);
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const resetAssistant = () => {
    setResponse(undefined);
    setInputValue("");
    setAnswerTyped(false);
    setRefdocHeadingTyped(false);
    setIsFocused(false);
  };

  const nextIndex = (currentIndex + 1) % textOptions.length;

  return (
    <div className="min-w-0 overflow-hidden -mt-3">
      {/* Input field area */}
      <div className="flex items-center">
        <div className="flex-1 min-w-0 flex items-center py-0 pr-1">
          <div className="flex-1 flex items-center gap-3 bg-white rounded-xl py-2 pl-4 pr-2 shadow-sm min-h-[56px]">
            <img src={wand} alt="" className="h-5 w-5 opacity-70 shrink-0" aria-hidden />
            <div 
              className="relative overflow-hidden flex-1 min-h-[24px] flex items-center cursor-text"
              onClick={() => {
                inputRef.current?.focus();
                setIsFocused(true);
              }}
            >
              {/* Input field - always present and visible */}
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                onKeyPress={handleKeyPress}
                className="w-full text-sm bg-transparent border-none outline-none relative z-10"
                style={{ 
                  color: '#1f2937',
                }}
                disabled={loading}
                placeholder=""
              />
              {/* Placeholder text overlay - only show when not focused and no input value */}
              {!isFocused && !inputValue && (
                <div className="absolute inset-0 pointer-events-none z-0 flex items-center">
                  {/* Current text - exits upward */}
                  {textOptions.length > 0 && (
                    <span
                      key={currentIndex}
                      className={`text-gray-800 w-full transition-all ease-in-out ${
                        isAnimating
                          ? "transform -translate-y-full opacity-0"
                          : "transform translate-y-0 opacity-100"
                      }`}
                      style={{
                        transitionDuration: isAnimating ? "600ms" : "0ms",
                      }}
                    >
                      {textOptions[currentIndex]}
                    </span>
                  )}
                  {/* Next text - enters from bottom */}
                  {nextTextVisible && textOptions.length > 0 && (
                    <span
                      ref={nextTextRef}
                      className="text-gray-800 w-full absolute left-0 transition-all ease-in-out"
                      style={{
                        transform: "translateY(100%)",
                        opacity: 0,
                        transitionDuration: "600ms",
                      }}
                    >
                      {textOptions[nextIndex]}
                    </span>
                  )}
                </div>
              )}
              {/* Invisible spacer to maintain height when placeholder is hidden */}
              {!(!isFocused && !inputValue) && textOptions.length > 0 && (
                <span className="invisible pointer-events-none absolute">
                  {textOptions[currentIndex]}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Character image or submit button - show button when text is typed, image otherwise */}
        <div className="relative shrink-0 overflow-hidden ml-2">
          {inputValue.trim() ? (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="h-12 w-12 rounded-full bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
              aria-label="Submit"
            >
              {loading ? (
                <RefreshIcon className="h-5 w-5 text-white animate-spin" />
              ) : (
                <ArrowRightIcon className="h-5 w-5 text-white" />
              )}
            </button>
          ) : (
            <div className="h-12 w-12 overflow-hidden">
              <img
                src={selectedAssistantImage}
                alt=""
                className="h-12 object-contain"
                style={{ 
                  transform: "scale(1.2) translate(0px, 2px)",
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="mt-4 text-gray-600 text-sm">
          <TypeText stylingClass="text-sm" delay={500} onComplete={() => {}}>
            Working on it...
          </TypeText>
        </div>
      )}

      {/* API Response - Answer */}
      {response && !loading && (
        <div className="mt-4">
          <div className="text-sm text-gray-800">
            {response.is_ambiguous ? (
              <TypeText onComplete={() => {
                setAnswerTyped(true);
              }}>
                Thanks for the request. Can you please revise your request to be more specific.
              </TypeText>
            ) : (
              <TypeText onComplete={() => {
                setAnswerTyped(true);
              }}>
                {response.answer}
              </TypeText>
            )}
          </div>

          {/* Reference Documents - automatically show after answer finishes typing */}
          {response.documents && answerTyped && (
            <div className="mt-4">
              {response.documents.length > 0 && (
                <TypeText onComplete={() => setRefdocHeadingTyped(true)}>
                  <div className="pt-2 pb-2">
                    {response.documents.length === 1
                      ? "Here is a QuickFlow (or document) that will help:"
                      : "Here are some QuickFlows (or documents) that will help:"}
                  </div>
                </TypeText>
              )}

              {refdocHeadingTyped && response.documents.length > 0 && (
                <StaggerList
                  onAnimationComplete={() => {}}
                  list={[...response.documents]
                    .sort((a, b) => {
                      // QuickFlow documents first
                      const aIsQuickFlow = (a.reftype || "").toUpperCase() === "QUICKFLOW";
                      const bIsQuickFlow = (b.reftype || "").toUpperCase() === "QUICKFLOW";
                      if (aIsQuickFlow && !bIsQuickFlow) return -1;
                      if (!aIsQuickFlow && bIsQuickFlow) return 1;
                      return 0;
                    })
                    .map((document: ReferenceDocumentEntity) => (
                      <div style={{ marginLeft: -7 }} key={document.id}>
                        <RefdocItem2
                          refdocCount={refdocCount}
                          id={document.id}
                          name={document.document_name}
                          url={document.url}
                          type={document.reftype || ""}
                        />
                      </div>
                    ))}
                />
              )}

              {/* New Request button - show after documents */}
              {refdocHeadingTyped && (
                <div className="mt-6">
                  <button
                    onClick={resetAssistant}
                    className="w-full px-4 py-2 border rounded-lg transition-colors"
                    style={{
                      borderColor: '#014ec7',
                      color: '#014ec7',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f0f9ff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    New Request
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
